import classnames from 'classnames'
import { h, Fragment } from 'preact'
import { useEffect, useState } from 'preact/compat'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { checkPaymentStatus } from '../../../api/payments'
import { paymentStatuses } from '../../../constants/paymentStatuses'
import { DataDog } from '../../../datadog'
import ErrorMessage from '../../Common/ErrorMessage'
import Loader from '../../Common/Loader'
import { useTranslations } from '../../../hooks/useTranslations'
import './styles.scss'
import useFailureRedirect from '../../../hooks/useFailureRedirect'
import useAuthoriseError from '../../../hooks/useAuthoriseError'
import {
  formatAsCurrency,
  getSupportPhoneNumber,
  redirect,
  sprintf,
} from '../../../helpers/common'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import { handlePayment } from '../../../helpers/payment'
import InfoMessage from '../../Common/InfoMessage'
import LinkButton from '../../Common/LinkButton'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'

const IDeal = ({
  handleSubmit,
  showPaymentConfirmation,
  isGoBackButtonVisible,
  loaderImage,
}) => {
  const l10n = useTranslations()

  const [error, setError] = useState('')
  const [isPaymentPending, setIsPaymentPending] = useState(false)
  const transactionDetails = useSelector(transactionDetailsSelector)
  const pageState = useSelector(pageStateSelector)
  const [isPaymentDisabled, setPaymentDisabled] = useState(true)
  const [isPaymentStatusAuthorised, setPaymentStatusAuthorised] =
    useState(false)
  const [isPaymentStatusPending, setIsPaymentStatusPending] = useState(true)

  const payButtonClasses = classnames('button', 'primary', 'medium', {
    loading: isPaymentStatusPending,
  })

  useEffect(() => {
    if (!pageState.isPaymentBlocked) {
      setError('')
    }
  }, [pageState.isPaymentBlocked])

  const handlePaymentStatus = () => {
    const isAdyen = transactionDetails?.adyenPaymentMethods?.includes(
      PAYMENT_METHOD_TYPES.IDeal,
    )
    setIsPaymentStatusPending(true)
    DataDog.info(
      `Request payment status of ${isAdyen ? 'Adyen Ideal' : 'Apexx IDeal'}`,
    )
    return checkPaymentStatus(
      isAdyen ? 'adyen/ideal' : 'ideal',
      transactionDetails.id,
    ).then(({ responseData }) => {
      DataDog.info('IDeal payment status response', responseData)
      if (responseData?.status === paymentStatuses.AUTHORISE_REQUESTED) {
        setPaymentDisabled(true)
        setPaymentStatusAuthorised(true)
      } else {
        setPaymentDisabled(false)
        setPaymentStatusAuthorised(false)
      }

      setIsPaymentStatusPending(false)
    })
  }

  useEffect(async () => {
    window.onpageshow = async event => {
      if (event.persisted) {
        DataDog.info('IDeal user went back to payment page')
        await handlePaymentStatus()
      }
    }

    await handlePaymentStatus()
  }, [])

  useFailureRedirect()
  const handleAuthoriseError = useAuthoriseError()

  const formatAmount = formatAsCurrency(
    transactionDetails.currency,
    transactionDetails.culture,
  )

  const currentAmount = formatAmount(
    transactionDetails?.overrideAmount
      ? transactionDetails.overrideAmount
      : transactionDetails.amount,
  )

  const paymentData = {
    pageState,
    setError,
    l10n,
    setIsPaymentPending,
    handleSubmit,
    transactionDetails,
    handleAuthoriseError,
    paymentMethod: PAYMENT_METHOD_TYPES.IDeal,
  }

  return (
    <>
      <div className="ideal-wrapper">
        {showPaymentConfirmation && (
          <InfoMessage
            message={sprintf(
              l10n.t(`You're about to pay %s with iDeal`),
              currentAmount,
            )}
            data-testid="ideal-info-message"
            className="ideal-info-message"
          />
        )}
        <ErrorMessage
          isShown={!!error}
          message={error}
          data-testid="ideal-error"
        />

        <ErrorMessage
          data-testid="ideal-payment-disabled-error"
          isShown={isPaymentStatusAuthorised}
          message={l10n.t(
            'Your payment request is currently being processed, if you are not redirected, please contact customer services on',
          )}
        >
          <div className="support-phone-number">
            {getSupportPhoneNumber(transactionDetails.culture)}
          </div>
        </ErrorMessage>

        {isPaymentPending ? (
          <Loader loaderImage={loaderImage} />
        ) : (
          <div className="ideal-button">
            {isGoBackButtonVisible && (
              <LinkButton
                data-testid="go-back-button"
                title={l10n.t('Go Back')}
                onClick={() => redirect(transactionDetails.returnUrl)}
                className="go-back-button"
              />
            )}
            <button
              type="button"
              className={payButtonClasses}
              data-testid="ideal-button"
              onClick={() => handlePayment(paymentData)}
              disabled={isPaymentDisabled}
            >
              {l10n.t('Pay Now')}
            </button>
          </div>
        )}
      </div>
    </>
  )
}

IDeal.propTypes = {
  handleSubmit: PropTypes.func,
  showPaymentConfirmation: PropTypes.bool,
  isGoBackButtonVisible: PropTypes.bool,
  loaderImage: PropTypes.string,
}

export default IDeal
